import { useCallback } from "react";

import { type CustomerModelOrganizationModel, Roles } from "@doitintl/cmp-models";
import { type ModelReference } from "@doitintl/models-any";

import { useAuthContext } from "../../Context/AuthContext";
import { useCustomerContext } from "../../Context/CustomerContext";
import { createRowsFilters, handleCustomTimeRange } from "../../Pages/CloudAnalytics/generateReport/generateReport";
import { type CreateReportParams, type CreateReportPayload } from "../../Pages/CloudAnalytics/generateReport/types";
import { colsMap, defaultConfig } from "../../Pages/CloudAnalytics/generateReport/utils";
import { type CustomerRef } from "../../types";
import { useReportMutationWithCustomerId } from "./cloudAnalytics/reports/useReports";

export const useGenerateReportWithCustomer = (
  customerRef: CustomerRef | null,
  userOrganizationRef: ModelReference<CustomerModelOrganizationModel> | null
) => {
  const { currentUser } = useAuthContext();
  const addReportMutation = useReportMutationWithCustomerId(customerRef?.id);
  /*
   two scenarios:
       1. we provide the payload to create report in full, then we do not need to process, this case is payloadReady = true.
       2. we provide the payload to create report in part, then we need to process, this case is payloadReady = false.
  */
  return useCallback(
    (requestReport: CreateReportParams | CreateReportPayload, payloadReady?: boolean) => {
      if (!customerRef) {
        return "";
      }

      const isPayloadReady = (
        paramRequestReport: CreateReportParams | CreateReportPayload
      ): paramRequestReport is CreateReportPayload => !!payloadReady;
      const email = currentUser?.email ? currentUser.email : "test@test.com";
      if (!isPayloadReady(requestReport)) {
        const { fields, ...config } = requestReport.config;
        const { rows, filters, optional } = createRowsFilters(fields);
        if (config.customTimeRange) {
          config.customTimeRange = handleCustomTimeRange(config.customTimeRange);
        }
        const fullRequestConfig = { ...config, rows, filters };
        const fullConfig = {
          ...defaultConfig,
          ...fullRequestConfig,
          optional,
        };
        fullConfig.cols = colsMap[fullConfig.timeInterval];
        const reportPayloadFromParams = {
          ...requestReport,
          customer: customerRef,
          config: fullConfig,
          collaborators: [{ email, role: Roles.OWNER }],
          organization: userOrganizationRef ?? null,
        };

        return addReportMutation.mutateAsync(reportPayloadFromParams);
      }

      const payload = {
        ...requestReport,
        customer: customerRef,
        organization: userOrganizationRef ?? null,
        collaborators: [{ email, role: Roles.OWNER }],
      };

      return addReportMutation.mutateAsync(payload);
    },
    [customerRef, currentUser?.email, userOrganizationRef, addReportMutation]
  );
};

const useGenerateReport = () => {
  const { userOrganization, customer } = useCustomerContext();
  const ref = userOrganization?.snapshot.ref;

  return useGenerateReportWithCustomer(customer.ref, ref ? ref : null);
};

export default useGenerateReport;
